import { makeStyles, mergeClasses, tokens, Tooltip } from "@fluentui/react-components";
import { CheckmarkCircle16Regular, ErrorCircle16Regular, Open16Regular, Subtract16Regular, Warning16Regular } from "@fluentui/react-icons";
import type { ReactNode } from "react";
import { FuiButton } from "@/components/fui-control";

export type CheckListStatus = "ok" | "watch" | "risk" | "na";

export interface CheckListItem {
  key: string;
  label: string;
  status: CheckListStatus;
  /** 값 칸. 없으면 text를 값으로 쓴다 */
  figure?: string;
  /** 값 옆 보조 */
  aside?: string;
  /** 기준 칸. 모든 행에 둔다 */
  mark?: string;
  /** 툴팁·폴백 문장 */
  text: string;
  /** 주의·위험의 자세한 기준. 칸에는 넣지 않고 툴팁에만 둔다 */
  rule?: string;
  /** 항목 이름 옆의 이동. 아이콘만 두고 이름은 툴팁·접근 이름에 둔다 */
  open?: { label: string; onClick: () => void };
}

const STATUS_LABEL: Record<CheckListStatus, string> = { ok: "정상", watch: "주의", risk: "위험", na: "판정 불가" };

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS, minWidth: 0 },
  meta: { fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, color: tokens.colorNeutralForeground3, whiteSpace: "nowrap" },
  /** 행이 contents라 칸 너비는 표 전체에서 맞는다 */
  table: {
    display: "grid",
    gridTemplateColumns: "16px max-content minmax(0, 1fr) 132px",
    columnGap: tokens.spacingHorizontalS,
    alignItems: "center",
    margin: 0,
    padding: 0,
    listStyle: "none",
    minWidth: 0,
  },
  row: { display: "contents" },
  cell: {
    minHeight: "28px",
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  headCell: {
    minHeight: "28px",
    borderTop: "none",
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground2,
    gap: tokens.spacingHorizontalXS,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  /** 칸 사이 틈까지 헤더 배경이 이어지게 한다 */
  headBleed: {
    marginLeft: `calc(${tokens.spacingHorizontalS} * -1)`,
    paddingLeft: tokens.spacingHorizontalS,
  },
  icon: { display: "inline-flex", alignItems: "center", justifyContent: "center", color: tokens.colorNeutralForeground3 },
  iconWatch: { color: tokens.colorPaletteDarkOrangeForeground1 },
  iconRisk: { color: tokens.colorPaletteRedForeground1 },
  label: { color: tokens.colorNeutralForeground2, whiteSpace: "nowrap", gap: tokens.spacingHorizontalXS },
  figure: {
    color: tokens.colorNeutralForeground1,
    fontVariantNumeric: "tabular-nums",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  aside: { marginLeft: tokens.spacingHorizontalS, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, color: tokens.colorNeutralForeground3, whiteSpace: "nowrap", flexShrink: 0 },
  quiet: { color: tokens.colorNeutralForeground3 },
  mark: { fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, color: tokens.colorNeutralForeground3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
});

function StatusIcon({ status }: { status: CheckListStatus }) {
  const st = useStyles();
  const icon = status === "risk" ? <ErrorCircle16Regular /> : status === "watch" ? <Warning16Regular /> : status === "ok" ? <CheckmarkCircle16Regular /> : <Subtract16Regular />;
  return (
    <span className={mergeClasses(st.icon, status === "risk" && st.iconRisk, status === "watch" && st.iconWatch)} role="img" aria-label={STATUS_LABEL[status]}>
      {icon}
    </span>
  );
}

/**
 * 항목별 점검 표. 상태 · 항목 · 값 · 기준이 행마다 같은 칸에 맞는다.
 * 색은 주의·위험 아이콘에만 쓴다.
 */
export function CheckList({ title, meta, items, ariaLabel }: { title: ReactNode; meta?: ReactNode; items: readonly CheckListItem[]; ariaLabel?: string }) {
  const st = useStyles();
  return (
    <div className={st.root} aria-label={ariaLabel}>
      <ul className={st.table}>
        <li className={st.row}>
          <span className={mergeClasses(st.cell, st.headCell)} />
          <span className={mergeClasses(st.cell, st.headCell, st.headBleed)}>
            {title}
            {meta ? <span className={st.meta}>{meta}</span> : null}
          </span>
          <span className={mergeClasses(st.cell, st.headCell, st.headBleed)}>값</span>
          <span className={mergeClasses(st.cell, st.headCell, st.headBleed)}>기준</span>
        </li>
        {items.map((it) => {
          const tip = it.rule ? `${it.text} (기준 ${it.rule})` : it.text;
          return (
            <li key={it.key} className={st.row}>
              <span className={st.cell}>
                <StatusIcon status={it.status} />
              </span>
              <span className={mergeClasses(st.cell, st.label)}>
                {it.label}
                {it.open && (
                  <Tooltip content={it.open.label} relationship="label">
                    <FuiButton appearance="subtle" icon={<Open16Regular />} aria-label={it.open.label} onClick={it.open.onClick} />
                  </Tooltip>
                )}
              </span>
              <span className={st.cell} title={tip}>
                <span className={mergeClasses(st.figure, it.status === "na" && st.quiet)}>{it.figure ?? it.text}</span>
                {it.aside && <span className={st.aside}>{it.aside}</span>}
              </span>
              <span className={mergeClasses(st.cell, st.mark)} title={it.rule ?? it.mark}>
                {it.mark ?? ""}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
